import React from 'react';

/**
 * Standardized inline spinner — iridescent conic-gradient ring in the V
 * palette (`.spinner-ring` in index.css). The ONLY sanctioned spinner for
 * modals, button submit states, and pre-shell auth pages where a skeleton
 * would misrepresent the layout. Page/section loading uses skeletons
 * (SkeletonLoader.tsx) instead. Hand-rolled border-trick spinners are
 * banned by a jest guard (no-handrolled-spinners.test.ts).
 */
type SpinnerSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

const THICKNESS: Record<SpinnerSize, string> = {
  sm: '2px',
  md: '3px',
  lg: '3.5px',
};

interface SpinnerProps {
  size?: SpinnerSize;
  /** Optional caption rendered under the ring (gray-400). */
  label?: string;
  className?: string;
}

export const Spinner: React.FC<SpinnerProps> = ({ size = 'md', label, className = '' }) => {
  const ring = (
    <div
      className={`spinner-ring ${SIZE_CLASSES[size]} ${label ? '' : className}`}
      style={{ '--spinner-thickness': THICKNESS[size] } as React.CSSProperties}
      role="status"
      aria-label={label || 'Loading'}
    />
  );

  if (!label) return ring;

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {ring}
      <p className="text-gray-400 text-sm">{label}</p>
    </div>
  );
};

export default Spinner;
